<script setup lang="ts">
import { useMapStore } from '@/stores/mapStore.ts'
import { computed, watch } from 'vue'
import { useShipStore } from '@/stores/shipStore.ts'
import { usePlanetStore } from '@/stores/planetStore.ts'
import { useTableStore } from '@/stores/tableStore.ts'
import PlanetMultiSelectMenu from '@/components/Interface/Menu/PlanetMultiSelectMenu.vue'
import ShipMultiSelectMenu from '@/components/Interface/Menu/ShipMultiSelectMenu.vue'
import MapFleetMenu from '@/components/Interface/Menu/MapFleetMenu.vue'
import { logger } from '@/models/logger';
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const mapStore = useMapStore()
const shipStore = useShipStore()
const planetStore = usePlanetStore()
const tableStore = useTableStore()

const fleetIds = computed(() => {
  const ids = new Set()
  for (const ship of Array.from(shipStore.ships.values())) {
    const { xPos, yPos, fleetId } = ship
    const withinXRange =
      Number(xPos) >= Number(mapStore.areaBoxTL.x) && Number(xPos) <= Number(mapStore.areaBoxRB.x)
    const withinYRange =
      Number(yPos) <= Number(mapStore.areaBoxTL.y) && Number(yPos) >= Number(mapStore.areaBoxRB.y)

    if (withinXRange && withinYRange && fleetId > 0) {
      ids.add(fleetId)
    }
  }
  return Array.from(ids)
})

const shipIds = computed(() => {
  const ids = [] // Use a different variable name to avoid redeclaration confusion
  for (const ship of shipStore.ships.values()) {
    if (
      Number(ship.xPos) >= Number(mapStore.areaBoxTL.x) &&
      Number(ship.xPos) <= Number(mapStore.areaBoxRB.x) &&
      Number(ship.yPos) <= Number(mapStore.areaBoxTL.y) &&
      Number(ship.yPos) >= Number(mapStore.areaBoxRB.y)
    ) {
      if (ship.fleetId === 0) {
        ids.push(ship.id)
      }
    }
  }
  return ids
})

watch(
  () => shipIds.value,
  (newShips) => {
    tableStore.ships.selectedRowIds = newShips
    logger.debug('Updated shipIds:', newShips)
  },
  { immediate: true } // Trigger the watcher initially if needed
)

const hasFleetsInArea = computed(() => {
  for (const ship of shipStore.ships.values()) {
    if (
      Number(ship.xPos) >= Number(mapStore.areaBoxTL.x) &&
      Number(ship.xPos) <= Number(mapStore.areaBoxRB.x) &&
      Number(ship.yPos) <= Number(mapStore.areaBoxTL.y) &&
      Number(ship.yPos) >= Number(mapStore.areaBoxRB.y)
    ) {
      if (ship.fleetId > 0) {
        return true
      }
    }
  }
  return false
})

const planetIds = computed(() => {
  let planets = []
  for (const planet of planetStore.planets.values()) {
    if (
      Number(planet.xPos) >= Number(mapStore.areaBoxTL.x) &&
      Number(planet.xPos) <= Number(mapStore.areaBoxRB.x) &&
      Number(planet.yPos) <= Number(mapStore.areaBoxTL.y) &&
      Number(planet.yPos) >= Number(mapStore.areaBoxRB.y)
    ) {
      planets.push(planet.id)
    }
  }
  return planets
})

// Watch and update only if `planetsInArea` changes
watch(
  () => planetIds.value,
  (newPlanets) => {
    tableStore.planets.selectedRowIds = newPlanets
  },
  { immediate: true } // Trigger the watch initially if needed
)

// Calculate adjusted position to keep menu on screen
const adjustedPosition = computed(() => {
  const baseX = mapStore.areaMouseEnd.x
  const baseY = mapStore.areaMouseEnd.y

  // Estimate menu width based on number of options (each icon is ~40px wide)
  let menuWidth = 0
  if (Object.keys(shipIds.value).length > 0) menuWidth += 40
  if (hasFleetsInArea.value) menuWidth += 40
  if (Object.keys(planetIds.value).length > 0) menuWidth += 40

  // Menu height is roughly 40px for a single row of icons
  const menuHeight = 40

  // Get the map container element to use its dimensions
  const mapContainer = document.querySelector('.webgl-container')
  if (!mapContainer) {
    return { x: baseX, y: baseY } // Fallback if container not found
  }

  const containerRect = mapContainer.getBoundingClientRect()
  const containerWidth = containerRect.width
  const containerHeight = containerRect.height

  // Calculate adjusted position
  let adjustedX = baseX
  let adjustedY = baseY

  // Check right boundary (relative to container)
  if (baseX + menuWidth > containerWidth) {
    adjustedX = containerWidth - menuWidth - 10 // 10px margin
  }

  // Check left boundary
  if (adjustedX < 10) {
    adjustedX = 10 // 10px margin
  }

  // Check bottom boundary (relative to container)
  if (baseY + menuHeight > containerHeight) {
    adjustedY = baseY - menuHeight - 10 // Position above the cursor with 10px margin
  }

  // Check top boundary
  if (adjustedY < 10) {
    adjustedY = 10 // 10px margin
  }

  return {
    x: adjustedX,
    y: adjustedY
  }
})
</script>

<template>
  <div
    v-if="
      Object.keys(fleetIds).length > 0 ||
      Object.keys(shipIds).length > 0 ||
      Object.keys(planetIds).length > 0
    "
    class="absolute z-20"
    :style="{ left: adjustedPosition.x + 'px', top: adjustedPosition.y + 'px' }"
  >
    <div class="flex">
      <div v-if="Object.keys(shipIds).length > 0">
        <ShipMultiSelectMenu :locked-to-fleet-id="0" :smart-positioning="true">
          <img
            src="https://cdn.ferion.com/images/map/icons/ship.png"
            :alt="t('map.tooltips.ships')"
            :title="t('map.tooltips.ships')"
            class="p-1 hover:bg-slate-800 bg-slate-700 cursor-pointer rounded mr-1"
          />
        </ShipMultiSelectMenu>
      </div>
      <div v-if="hasFleetsInArea">
        <MapFleetMenu :fleet-ids="fleetIds" :smart-positioning="true">
          <img
            src="https://cdn.ferion.com/images/map/icons/fleet.png"
            :alt="t('map.tooltips.fleets')"
            :title="t('map.tooltips.fleets')"
            class="p-1 hover:bg-slate-800 bg-slate-700 cursor-pointer rounded mr-1"
          />
        </MapFleetMenu>
      </div>
      <div v-if="Object.keys(planetIds).length > 0">
        <PlanetMultiSelectMenu :smart-positioning="true">
          <img
            src="https://cdn.ferion.com/images/map/icons/planet.png"
            :alt="t('map.tooltips.planetsCount', { count: Object.keys(planetIds).length })"
            :title="t('map.tooltips.planetsCount', { count: Object.keys(planetIds).length })"
            class="p-1 hover:bg-slate-800 bg-slate-700 cursor-pointer rounded mr-1"
          />
        </PlanetMultiSelectMenu>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss"></style>
